// src/features/comment/commentSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

// Thunks
// Add skip/limit args
export const fetchComments = createAsyncThunk(
  "comment/fetchComments",
  async ({ postId, skip = 0, limit = 20 }, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/comment/all/${postId}?skip=${skip}&limit=${limit}`);
      return { newComments: res.data, skip };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || "Failed to fetch comments");
    }
  }
);

export const createComment = createAsyncThunk(
  "comment/createComment",
  async ({ text, post_id, content_id }, thunkAPI) => {
    try {
      const res = await axiosInstance.post("/comment", {
        text,
        post_id,
        content_id,
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || "Failed to create comment");
    }
  }
);

export const updateComment = createAsyncThunk(
  "comment/updateComment",
  async ({ comment_id, text, post_id, content_id }, thunkAPI) => {
    try {
      const res = await axiosInstance.put(`/comment/${comment_id}`, {
        text,
        post_id,
        content_id,
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || "Failed to update comment");
    }
  }
);

export const deleteComment = createAsyncThunk(
  "comment/deleteComment",
  async (comment_id, thunkAPI) => {
    try {
      await axiosInstance.delete(`/comment/${comment_id}`);
      return comment_id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || "Failed to delete comment");
    }
  }
);

// Slice
const commentSlice = createSlice({
  name: "comment",
  initialState: {
    items: [],
    loading: false,
    error: null,
    hasMore: true,
    skip: 0,
    limit: 10, // initial page size
    totalFetched: 0,
  },
  reducers: {
    clearCommentError(state) {
      state.error = null;
    },
    resetComments(state) {
      state.items = [];
      state.error = null;
      state.loading = false;
      state.hasMore = true;
      state.skip = 0;
      state.totalFetched = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchComments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.loading = false;
        const { newComments, skip } = action.payload;
        // If skip == 0, reset; if skip > 0, append
        if (skip === 0) {
          state.items = newComments;
          state.totalFetched = newComments.length;
        } else {
          state.items = [...state.items, ...newComments];
          state.totalFetched += newComments.length;
        }
        state.hasMore = newComments.length === state.limit;
        state.skip = skip + newComments.length;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createComment.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })

      // Update
      .addCase(updateComment.fulfilled, (state, action) => {
        state.items = state.items.map((c) =>
          c.id === action.payload.id ? action.payload : c
        );
      })

      // Delete
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c.id !== action.payload);
      });
  },
});

export const { clearCommentError, resetComments } = commentSlice.actions;
export default commentSlice.reducer;
